/**
 * Catálogo de cosméticos. Todos son gratuitos: algunos se desbloquean subiendo de nivel.
 * Los cosméticos son puramente visuales y no alteran la jugabilidad.
 */
export interface CosmeticOption {
  id: string;
  name: string;
  unlockLevel: number;
}

const opt = (id: string, name: string, unlockLevel = 1): CosmeticOption => ({ id, name, unlockLevel });

export const BODY_MODELS = [opt('classic', 'Clásico'), opt('chunky', 'Fortachón'), opt('mini', 'Mini')];

export const HAIR_STYLES = [
  opt('none', 'Calvo'),
  opt('spiky', 'Pincho'),
  opt('bob', 'Melena'),
  opt('afro', 'Afro'),
  opt('mohawk', 'Cresta'),
  opt('ponytail', 'Coleta'),
  opt('curly', 'Rizos', 2),
];

export const HATS = [
  opt('none', 'Sin sombrero'),
  opt('cap', 'Gorra'),
  opt('beanie', 'Gorro de lana'),
  opt('tophat', 'Chistera'),
  opt('cone', 'Cono de tráfico', 2),
  opt('propeller', 'Gorro de hélice', 3),
  opt('chef', 'Gorro de chef', 4),
  opt('crown', 'Corona Kokun', 6),
];

export const GLASSES = [
  opt('none', 'Sin gafas'),
  opt('round', 'Redondas'),
  opt('shades', 'De sol'),
  opt('star', 'Estrella', 3),
  opt('monocle', 'Monóculo', 5),
];

export const SHIRTS = [opt('tee', 'Camiseta'), opt('stripes', 'Rayas'), opt('hoodie', 'Sudadera'), opt('hawaii', 'Hawaiana', 2), opt('suit', 'Traje', 4)];

export const BACKPACKS = [
  opt('none', 'Sin mochila'),
  opt('classic', 'Mochila'),
  opt('shell', 'Caparazón', 3),
  opt('jetpack', 'Jetpack de cartón', 5),
  opt('balloon', 'Globo', 2),
];

export const ACCESSORIES = [
  opt('none', 'Nada'),
  opt('mustache', 'Bigote'),
  opt('duck', 'Patito de goma'),
  opt('banana', 'Plátano en la oreja', 2),
  opt('fish', 'Pez', 4),
];

export const EXPRESSIONS = [opt('happy', 'Feliz'), opt('neutral', 'Serio'), opt('mischief', 'Travieso'), opt('surprised', 'Sorprendido'), opt('angry', 'Gruñón', 2)];

export const SKIN_TONES = [0xffdbac, 0xf1c27d, 0xe0ac69, 0xc68642, 0x8d5524, 0x5c3a1e, 0xa0e7a0, 0xa0c4ff];
export const PALETTE = [
  0xff006e, 0xfb5607, 0xffbe0b, 0x8ac926, 0x06d6a0, 0x118ab2, 0x3a86ff, 0x8338ec, 0xf15bb5, 0xffffff, 0x2b2d42, 0x6c584c,
];
export const HAIR_COLORS = [0x2b1b17, 0x6f4e37, 0xd4a017, 0xb7410e, 0xe5e5e5, 0xff006e, 0x3a86ff, 0x06d6a0];

/** Configuración visual de un personaje (persistida y compartida con la sala). */
export interface CharacterConfig {
  body: string;
  skin: number;
  hair: string;
  hairColor: number;
  hat: string;
  glasses: string;
  shirt: string;
  shirtColor: number;
  pantsColor: number;
  shoesColor: number;
  backpack: string;
  accessory: string;
  expression: string;
  /** Escala visual de la cabeza: 0.9 – 1.3. */
  headScale: number;
  /** Usar el rostro fotográfico (si existe). */
  useFace: boolean;
}

export const DEFAULT_CHARACTER: CharacterConfig = {
  body: 'classic',
  skin: 0,
  hair: 'spiky',
  hairColor: 0,
  hat: 'none',
  glasses: 'none',
  shirt: 'tee',
  shirtColor: 0,
  pantsColor: 6,
  shoesColor: 9,
  backpack: 'none',
  accessory: 'none',
  expression: 'happy',
  headScale: 1.1,
  useFace: true,
};

export const COSMETIC_SLOTS = {
  body: BODY_MODELS,
  hair: HAIR_STYLES,
  hat: HATS,
  glasses: GLASSES,
  shirt: SHIRTS,
  backpack: BACKPACKS,
  accessory: ACCESSORIES,
  expression: EXPRESSIONS,
} as const;

export type CosmeticSlot = keyof typeof COSMETIC_SLOTS;

/** Devuelve el primer cosmético que el nivel no permite, o null si la configuración es válida. */
export function findLockedCosmetic(cfg: CharacterConfig, level: number): string | null {
  for (const [slot, options] of Object.entries(COSMETIC_SLOTS) as Array<[CosmeticSlot, CosmeticOption[]]>) {
    const chosen = options.find((o) => o.id === cfg[slot]);
    if (!chosen) return `${slot}:${String(cfg[slot])}`;
    if (chosen.unlockLevel > level) return `${slot}:${chosen.id}`;
  }
  return null;
}

/** Personaje aleatorio con cosméticos de nivel 1 (para invitados nuevos y el menú). */
export function randomCharacter(rand: () => number = Math.random): CharacterConfig {
  const pick = <T extends CosmeticOption>(list: T[]) => {
    const free = list.filter((o) => o.unlockLevel <= 1);
    return free[Math.floor(rand() * free.length)].id;
  };
  const idx = (n: number) => Math.floor(rand() * n);
  return {
    body: pick(BODY_MODELS),
    skin: idx(6),
    hair: pick(HAIR_STYLES),
    hairColor: idx(HAIR_COLORS.length),
    hat: rand() < 0.5 ? 'none' : pick(HATS),
    glasses: rand() < 0.6 ? 'none' : pick(GLASSES),
    shirt: pick(SHIRTS),
    shirtColor: idx(PALETTE.length),
    pantsColor: idx(PALETTE.length),
    shoesColor: idx(PALETTE.length),
    backpack: rand() < 0.6 ? 'none' : pick(BACKPACKS),
    accessory: rand() < 0.7 ? 'none' : pick(ACCESSORIES),
    expression: pick(EXPRESSIONS),
    headScale: 1 + Math.round(rand() * 20) / 100,
    useFace: false,
  };
}
